import moment from 'moment';
import { useState } from 'react';

import { FormFieldDatePicker } from 'Components/FormField/FormFieldDatePicker/FormFieldDatePicker';
import { Text } from 'Components/Text/Text';
import { TPRM_IRQ_DUE_DATE_WITHOUT_RECOMMENDATION, TPRM_IRQ_DUE_DATE_WITH_RECOMMENDATION, UCRM_IRQ_DUE_DATE_WITHOUT_RECOMMENDATION, UCRM_IRQ_DUE_DATE_WITH_RECOMMENDATION } from 'Config/Tooltips';
import { iso8601ToJsDate } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getNextDueDateInIsoFormat } from 'Helpers/ScheduleFrequency/ScheduleFrequency';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ScheduleFrequency } from 'Models/ScheduleFrequency';

export type ArgsForGettingIrqTargetCompletionDateSelectionProps =
    | {
          action: 'updateExistingSelection';
          currentAssessmentWorkflowIrqTargetCompletionDate?: string;
          expectedIrqDurationInDays?: never;
      }
    | {
          action: 'startServiceAssessmentWorkflow';
          currentAssessmentWorkflowIrqTargetCompletionDate?: string;
          expectedIrqDurationInDays?: number;
      };

/** Based on the state of an assessment subject (TPRM third-party service or UCRM use case) and the user's action being taken on it, returns an array of two (possibly undefined) dates: the recommended IRQ due date and the default IRQ due date. */
const getDates = (args: ArgsForGettingIrqTargetCompletionDateSelectionProps): [Date | undefined, Date | undefined] => {
    const recommendedDate = args.expectedIrqDurationInDays ? iso8601ToJsDate(getNextDueDateInIsoFormat(new Date(), args.expectedIrqDurationInDays.toString(), ScheduleFrequency.DAYS)) : undefined;
    const currentTargetDate = args.currentAssessmentWorkflowIrqTargetCompletionDate ? iso8601ToJsDate(args.currentAssessmentWorkflowIrqTargetCompletionDate) : undefined;
    const defaultDate = args.action === 'startServiceAssessmentWorkflow' && currentTargetDate === undefined ? recommendedDate : currentTargetDate;
    return [recommendedDate, defaultDate];
};

export const getDefaultIrqTargetCompletionDate = (args: ArgsForGettingIrqTargetCompletionDateSelectionProps): Date | undefined => {
    return getDates(args)[1];
};

/** `module` determines the wording of the tooltip. */
export const getIrqTargetCompletionDateSelectionProps = (module: DueDiligenceModule, args: ArgsForGettingIrqTargetCompletionDateSelectionProps, onDateSelected: (date: Date | undefined) => void): IrqTargetCompletionDateSelectionProps => {
    const [recommendedDate, defaultDate] = getDates(args);
    const isUcrm = module === ProductModule.UCRM;
    // A recommendation is only shown when starting a due diligence workflow.
    const tooltipText = args.action === 'startServiceAssessmentWorkflow' ? (isUcrm ? UCRM_IRQ_DUE_DATE_WITH_RECOMMENDATION : TPRM_IRQ_DUE_DATE_WITH_RECOMMENDATION) : isUcrm ? UCRM_IRQ_DUE_DATE_WITHOUT_RECOMMENDATION : TPRM_IRQ_DUE_DATE_WITHOUT_RECOMMENDATION;

    const recommendationText = (() => {
        if (args.action === 'startServiceAssessmentWorkflow' && recommendedDate) {
            return `Recommended: ${moment(recommendedDate).format('MM/DD/YYYY')}`;
        } else {
            return undefined;
        }
    })();

    return { defaultDate, onDateSelected, recommendationText, tooltipText };
};

export interface IrqTargetCompletionDateSelectionProps {
    defaultDate?: Date;
    onDateSelected: (date: Date | undefined) => void;
    recommendationText?: string;
    tooltipText?: string;
}

/**
 * Renders a date picker, a tooltip, and (maybe) recommendation text.
 * The date selected in the date picker will be used to set the due date for the assessment subject's IRQ.
 *
 * Used in the following scenarios:
 * * The user is creating a new service or use case but is not submitting the IRQ.
 * * The user wants to update the date from the Third Parties and Services or Businesses and Use Cases listing.
 * * The user is starting or restarting the due diligence workflow from the Service or Use Case Dashboard.
 *
 * The date populated by default and the recommendation text follow these rules:
 * * If the user is simply updating their existing selection, the date is pre-populated with the date already set by the user (which may be undefined), and there is no recommendation text.
 * * If the user is starting a new due diligence workflow, the recommendation text recommends the date based on the IRQ due date configured in Settings, and:
 * * * If the user has already set a date, the date picker is pre-populated with that date.
 * * * Otherwise, the date picker is pre-populated with the recommended date (which may be undefined).
 */
export const IrqTargetCompletionDateSelection = (props: IrqTargetCompletionDateSelectionProps) => {
    const [selectedIrqTargetCompletionDate, setSelectedIrqTargetCompletionDate] = useState<Date | undefined>(props.defaultDate);

    return (
        <>
            <FormFieldDatePicker
                dateFormat="MM/dd/yyyy"
                selected={selectedIrqTargetCompletionDate}
                handleChange={(selectedDate: Date | undefined | null) => {
                    setSelectedIrqTargetCompletionDate(selectedDate ?? undefined);
                    props.onDateSelected(selectedDate ?? undefined);
                }}
                formFieldId="irqDueDateSelection"
                formFieldLabel="Inherent Risk Questionnaire Due Date"
                placeholder={'MM/DD/YYYY'}
                tooltip={props.tooltipText}
            />
            {props.recommendationText && <Text color="darkGray">{props.recommendationText}</Text>}
        </>
    );
};
